/**
 * SSE-aware polling for unread chat count. Real-time updates via SSE when
 * connected; falls back to 10s polling when SSE is down.
 *
 * Two hooks:
 *  - `useUnreadCount` — owns the polling lifecycle. Mount once in BadgeProvider.
 *  - `useUnreadCountValue` — read-only subscriber for components that just need
 *    the current value. Avoids spinning up a duplicate polling timer (each
 *    QueryObserver maintains its own `refetchInterval`).
 */

import { useQuery, skipToken } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { useEventStreamState } from './useEventStreamState';
import { AuthSessionState } from '@providers/auth/authTypes';
import { customInstance } from '@api-client/api-client';
import type { UnreadCountResponse } from '@models/api';

/**
 * Canonical query key for the unread chat count. Exported so other modules
 * (markAsRead invalidation, SSE invalidation handler) reference the same
 * literal — prevents drift if the key ever changes.
 */
export const UNREAD_COUNT_KEY = ['chats', 'unread-count'] as const;

/**
 * Hook for polling unread chat count. Mount once globally in BadgeProvider.
 *
 * @param enabled - Whether to enable polling (default: true)
 * @param pollingInterval - Polling interval in milliseconds (default: 10000 = 10 seconds)
 * @returns React Query result with unread count data
 */
export function useUnreadCount(enabled = true, pollingInterval = 10000) {
  const { user, sessionState } = useAuth();
  const { isConnected } = useEventStreamState();
  // Issue #567: Stop polling during recovery so the invalid_grant cascade
  // doesn't get amplified by 10-second tokenless requests.
  const isSessionBlocked =
    sessionState === AuthSessionState.SESSION_EXPIRED ||
    sessionState === AuthSessionState.REAUTHENTICATING;
  const shouldPoll = enabled && !isSessionBlocked;

  return useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: async () => {
      const response = await customInstance<UnreadCountResponse>({
        url: '/api/v1/chats/unread-count',
        method: 'GET',
      });
      return response.data;
    },
    enabled: !!user && shouldPoll,

    // AIDEV-NOTE: Two orthogonal gates — SSE absorbs real-time updates when connected,
    // and Issue #567 pauses polling during auth recovery. Both must be satisfied to poll.
    refetchInterval: isConnected ? false : shouldPoll ? pollingInterval : false,
    refetchIntervalInBackground: false, // Foreground reconnect + full-sync handles catch-up
    refetchOnMount: 'always', // CRITICAL: Fetch immediately on mount for instant badge
    refetchOnWindowFocus: true, // Refetch when user returns to tab

    staleTime: pollingInterval * 0.8, // Consider data fresh for 80% of interval
    gcTime: pollingInterval * 2, // Keep in cache for 2x interval
  });
}

/**
 * Read-only subscriber for the unread chat count cache.
 *
 * Returns `data` reactively from the same cache key as `useUnreadCount`.
 * Does NOT spin up its own polling timer — relies on `useUnreadCount`
 * (mounted in BadgeProvider) to drive fetches. Use this in components
 * that consume the count but don't own the polling lifecycle (Header,
 * BottomNavigation).
 */
export function useUnreadCountValue() {
  return useQuery<UnreadCountResponse>({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: skipToken,
  });
}
